// Lib-free-ish report exporters used by the Timekeeping Reports tab.
// CSV + Excel (SpreadsheetML 2003 .xls, opens natively in Excel, no dependency)
// + PDF (via the already-bundled jspdf).
import { jsPDF } from "jspdf";

export type Cell = string | number;

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadCsv(filename: string, rows: Cell[][]) {
  const escape = (v: Cell) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
}

function xmlEscape(v: Cell): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}

/** SpreadsheetML 2003 workbook — a single .xls file Excel opens without warnings. */
export function downloadXls(filename: string, sheetName: string, rows: Cell[][]) {
  const cell = (v: Cell) => {
    const isNum = typeof v === "number" && Number.isFinite(v);
    return `<Cell><Data ss:Type="${isNum ? "Number" : "String"}">${xmlEscape(v)}</Data></Cell>`;
  };
  const body = rows.map((r) => `<Row>${r.map(cell).join("")}</Row>`).join("");
  const xml =
    `<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
    `<Worksheet ss:Name="${xmlEscape(sheetName).slice(0, 31)}"><Table>${body}</Table></Worksheet></Workbook>`;
  triggerDownload(new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8;" }), filename);
}

/** Simple paginated table PDF. First row of `rows` is treated as the header. */
export function downloadPdf(
  filename: string,
  title: string,
  subtitle: string,
  rows: Cell[][],
  colWidths?: number[],
) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 40;
  let y = 48;

  doc.setFontSize(16);
  doc.text(title, marginX, y);
  y += 18;
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(subtitle, marginX, y);
  doc.setTextColor(0);
  y += 20;

  const header = rows[0] ?? [];
  const dataRows = rows.slice(1);
  const cols = header.length || 1;
  const usableW = pageW - marginX * 2;
  const widths = colWidths && colWidths.length === cols
    ? colWidths
    : Array.from({ length: cols }, () => usableW / cols);
  const xs: number[] = [];
  let acc = marginX;
  for (const w of widths) { xs.push(acc); acc += w; }

  const drawRow = (cells: Cell[], bold: boolean) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(9);
    cells.forEach((c, i) => {
      const text = String(c ?? "");
      doc.text(text.length > 32 ? text.slice(0, 31) + "…" : text, xs[i], y);
    });
    y += 16;
  };

  drawRow(header, true);
  doc.setDrawColor(200);
  doc.line(marginX, y - 11, pageW - marginX, y - 11);

  for (const r of dataRows) {
    if (y > pageH - 40) {
      doc.addPage();
      y = 48;
      drawRow(header, true);
      doc.line(marginX, y - 11, pageW - marginX, y - 11);
    }
    drawRow(r, false);
  }

  doc.save(filename);
}
